export type WorkspaceInviteRegistrationForm = {
  cpf: string;
  email: string;
  emailConfirmed: boolean;
  name: string;
  nickName: string;
  phoneNumber: string;
  surname: string;
};

export type WorkspaceInviteRegistrationErrors = Partial<
  Record<keyof WorkspaceInviteRegistrationForm, string>
>;

export function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

export function formatCpf(value: string) {
  return onlyDigits(value)
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

export function formatPhone(value: string) {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }

  return digits
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
}

export function validateRegistration(
  values: WorkspaceInviteRegistrationForm,
): WorkspaceInviteRegistrationErrors {
  const errors: WorkspaceInviteRegistrationErrors = {};

  if (!values.nickName.trim()) errors.nickName = "Informe o apelido.";
  if (!values.name.trim()) errors.name = "Informe o nome.";
  if (!values.surname.trim()) errors.surname = "Informe o sobrenome.";
  if (!isEmail(values.email)) errors.email = "Informe um e-mail válido.";
  if (onlyDigits(values.phoneNumber).length < 10) {
    errors.phoneNumber = "Informe um telefone válido.";
  }
  if (!isValidCpf(values.cpf)) errors.cpf = "Informe um CPF válido.";
  if (!values.emailConfirmed) {
    errors.emailConfirmed = "Confirme que o e-mail está correto.";
  }

  return errors;
}

export function toRegistrationRequest(values: WorkspaceInviteRegistrationForm) {
  return {
    cpf: onlyDigits(values.cpf),
    email: values.email.trim().toLowerCase(),
    emailConfirmed: values.emailConfirmed,
    name: values.name.trim(),
    nickName: values.nickName.trim(),
    phoneNumber: onlyDigits(values.phoneNumber),
    surname: values.surname.trim(),
  };
}

export function isWorkspaceInvitationPath(path: string) {
  return /^\/auth\/convite\/[^/?#]+(?:[/?#]|$)/.test(path);
}

export function workspaceInviteLink(
  created: { token?: string | null; url?: string | null },
  origin: string,
) {
  const readyUrl = created.url?.trim();
  if (readyUrl) return readyUrl;

  const token = created.token?.trim();
  if (!token) return "";
  return `${origin.replace(/\/+$/, "")}/auth/convite/${encodeURIComponent(token)}`;
}

function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidCpf(value: string) {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

  const calculateDigit = (length: number) => {
    let sum = 0;
    for (let index = 0; index < length; index += 1) {
      sum += Number(cpf[index]) * (length + 1 - index);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return (
    calculateDigit(9) === Number(cpf[9]) &&
    calculateDigit(10) === Number(cpf[10])
  );
}
